import { useEffect, useState } from "react";
import { http } from "../../axios/axios";
import { toast } from "react-toastify";
import {
  Boxes,
  Layers,
  Package,
  Tags,
  Wrench,
  FolderKanban,
  Lightbulb,
  Newspaper,
  ClipboardList,
  TriangleAlert,
  Mail,
  Briefcase,
  Star,
  ChevronRight,
} from "lucide-react";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useNavigate } from "react-router-dom";

function StatsCard({ label, value, icon: Icon, color = "blue" }) {
  return (
    <div className="group relative overflow-hidden rounded-xl border border-slate-200 bg-slate-300 p-4 sm:p-5 transition-all duration-300 hover:border-blue-400 hover:shadow-lg">
      <div
        className={`absolute top-0 right-0 h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-${color}-500/10 blur-3xl transition-colors group-hover:bg-${color}-500/20`}
      />
      <div className="flex items-center gap-3">
        <div
          className={`hidden md:flex h-9 w-9 items-center justify-center rounded-lg bg-white text-${color}-600`}
        >
          <Icon className="h-4.5 w-4.5" />
        </div>

        <p className="text-[11px] sm:text-xs font-bold uppercase tracking-wide text-slate-600 leading-tight">
          {label}
        </p>
      </div>
      <h3 className="mt-3 sm:mt-5 text-2xl sm:text-3xl font-black text-slate-900">
        {value}
      </h3>
    </div>
  );
}

const LatestCard = ({
  title,
  items,
  field,
  onViewAll,
  showStatus = false,
  showRating = false,
}) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "bg-green-500/20 text-green-300 border border-green-500/30";

      case "pending":
        return "bg-yellow-500/20 text-yellow-300 border border-yellow-500/30";

      case "rejected":
        return "bg-red-500/20 text-red-300 border border-red-500/30";

      default:
        return "bg-slate-700 text-slate-300 border border-slate-600";
    }
  };

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-900/40 overflow-hidden transition-all duration-300 hover:border-blue-400 hover:shadow-lg">
      <div className="flex items-center justify-between border-b border-slate-700 bg-slate-800/70 px-4 py-3 sm:px-5 sm:py-4">
        <h2 className="truncate pr-3 text-sm font-semibold text-white sm:text-base lg:text-lg">
          {title}
        </h2>

        <button
          onClick={onViewAll}
          className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-blue-400 transition hover:bg-slate-700 hover:text-blue-300 sm:px-3 sm:py-1.5 sm:text-sm"
        >
          <span className="hidden xs:inline">View All</span>
          <span className="xs:hidden">View</span>
          <ChevronRight size={14} className="sm:h-4 sm:w-4" />
        </button>
      </div>

      <div className="divide-y divide-slate-700">
        {items?.length ? (
          items.map((item) => (
            <div
              key={item._id}
              className="flex flex-col gap-2 px-4 py-3 transition-colors hover:bg-slate-800/60 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5"
            >
              <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3 overflow-hidden">
                <p
                  title={item[field]}
                  className="min-w-0 flex-1 truncate text-xs font-medium text-slate-300 sm:text-sm lg:text-[15px]"
                >
                  {item[field]}
                </p>

                {showRating && (
                  <div className="flex shrink-0 items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        size={12}
                        className={`sm:h-[13px] sm:w-[13px] lg:h-[14px] lg:w-[14px] ${
                          star <= item.rating
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-slate-600"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {showStatus && (
                <span
                  className={`w-fit shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-semibold sm:px-3 sm:text-xs ${getStatusColor(
                    item.status,
                  )}`}
                >
                  {item.status}
                </span>
              )}
            </div>
          ))
        ) : (
          <div className="px-5 py-8 text-center text-sm text-slate-400">
            No data available
          </div>
        )}
      </div>
    </div>
  );
};

const DashboardChart = ({ title, data }) => {
  const max = Math.max(...data.map((d) => d.total), 1);

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-5">
      <h2 className="text-lg font-semibold text-white mb-6">{title}</h2>

      <div className="space-y-5">
        {data.map((item, index) => (
          <div key={index}>
            <div className="flex justify-between mb-2">
              <span className="text-sm text-slate-300 truncate mr-3">
                {item.name}
              </span>

              <span className="text-sm font-semibold text-white">
                {item.total}
              </span>
            </div>

            <div className="h-2 rounded-full bg-slate-700 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-400"
                style={{
                  width: `${(item.total / max) * 100}%`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [dashData, setDashData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchDashData = async () => {
    try {
      setLoading(true);

      const res = await http("dashboard");

      if (res.data.success) {
        setDashData(res.data?.data);
      } else {
        toast.error(res.data?.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashData();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-700 border-t-blue-500" />
          <p className="text-sm text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen p-2 md:p-4 text-slate-200">
      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-4 sm:px-6 sm:py-5 mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white truncate font-serif">
            Welcome back, Admin 👋
          </h1>

          <p className="mt-1 text-xs sm:text-sm text-slate-400 leading-relaxed font-serif">
            Manage your products, services, inquiries and website activity.
          </p>
        </div>

        <div className="flex sm:block sm:text-right">
          <div>
            <p className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-wide font-serif">
              Dashboard
            </p>

            <p className="text-sm sm:text-base font-semibold text-blue-400 font-serif">
              Suraksha 24/7
            </p>
          </div>
        </div>
      </div>

      {/** Dashboard Overview */}
      <div className="mt-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-700" />

          <div className="text-center">
            <h2 className="text-xl font-bold text-white">Dashboard Overview</h2>
            <p className="mt-1 text-sm text-slate-400">
              Quick summary of your business data
            </p>
          </div>

          <div className="h-px flex-1 bg-slate-700" />
        </div>
        <div className="rounded-2xl border border-slate-800 bg-slate-900/30 p-5">
          <div className="grid grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
            <StatsCard
              label="Total Product Inquiry"
              value={dashData?.totalProductInquiry || 0}
              icon={ClipboardList}
              color="emerald"
            />

            <StatsCard
              label="Total Complaint"
              value={dashData?.totalComplaint || 0}
              icon={TriangleAlert}
              color="red"
            />

            <StatsCard
              label="Total Contacts"
              value={dashData?.totalContact || 0}
              icon={Mail}
              color="sky"
            />

            <StatsCard
              label="Total Reviews"
              value={dashData?.totalReview || 0}
              icon={Star}
              color="amber"
            />

            <StatsCard
              label="Total Products"
              value={dashData?.totalProducts || 0}
              icon={Package}
              color="green"
            />

            <StatsCard
              label="Total Services"
              value={dashData?.totalServices || 0}
              icon={Wrench}
              color="cyan"
            />

            <StatsCard
              label="Total Projects"
              value={dashData?.totalProjects || 0}
              icon={FolderKanban}
              color="indigo"
            />

            <StatsCard
              label="Total Solutions"
              value={dashData?.totalSolutions || 0}
              icon={Lightbulb}
              color="yellow"
            />

            <StatsCard
              label="Total Blogs"
              value={dashData?.totalBlogs || 0}
              icon={Newspaper}
              color="pink"
            />

            <StatsCard
              label="Total Applications"
              value={dashData?.totalApplication || 0}
              icon={Briefcase}
              color="violet"
            />

            <StatsCard
              label="Total Categories"
              value={dashData?.totalCategories || 0}
              icon={Layers}
              color="purple"
            />

            <StatsCard
              label="Total Sub Categories"
              value={dashData?.totalSubCategories || 0}
              icon={Boxes}
              color="orange"
            />

            <StatsCard
              label="Total Brands"
              value={dashData?.totalBrands || 0}
              icon={Tags}
              color="blue"
            />
          </div>
        </div>
      </div>

      {/** Analytics Overview */}
      <div className="mt-12">
        <div className="mb-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-700" />

          <div className="text-center">
            <h2 className="text-xl font-bold text-white">Analytics Overview</h2>
            <p className="mt-1 text-sm text-slate-400">
              Track your top performing brands and categories
            </p>
          </div>

          <div className="h-px flex-1 bg-slate-700" />
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/30 p-5">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <DashboardChart
              title="Top 5 Brands"
              data={dashData?.topBrands || []}
              dataKey="name"
            />

            <DashboardChart
              title="Top 5 Categories"
              data={dashData?.topCategories || []}
              dataKey="name"
            />
          </div>
        </div>
      </div>

      {/** Latest Activities */}
      <div className="mt-12">
        <div className="mb-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-700" />

          <div className="text-center">
            <h2 className="text-xl font-bold text-white">Latest Activities</h2>
            <p className="mt-1 text-sm text-slate-400">
              Recent inquiries, updates and content changes
            </p>
          </div>

          <div className="h-px flex-1 bg-slate-700" />
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/30 p-5">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <LatestCard
              title="Latest Product Inquiries"
              items={dashData.latestProductInquiry}
              field="name"
              showStatus
              onViewAll={() => navigate("/admin/product-inquiry")}
            />

            <LatestCard
              title="Latest Complaints"
              items={dashData.latestComplaint}
              field="name"
              showStatus
              onViewAll={() => navigate("/admin/complaints")}
            />

            <LatestCard
              title="Latest Contacts"
              items={dashData.latestContact}
              field="name"
              showStatus
              onViewAll={() => navigate("/admin/contact")}
            />

            <LatestCard
              title="Latest Reviews"
              items={dashData.latestReview}
              field="name"
              showStatus
              showRating
              onViewAll={() => navigate("/admin/reviews")}
            />

            <LatestCard
              title="Latest Products"
              items={dashData.latestProducts}
              field="productName"
              onViewAll={() => navigate("/admin/products")}
            />

            <LatestCard
              title="Latest Services"
              items={dashData.latestServices}
              field="serviceName"
              onViewAll={() => navigate("/admin/service")}
            />

            <LatestCard
              title="Latest Projects"
              items={dashData.latestProjects}
              field="title"
              onViewAll={() => navigate("/admin/project")}
            />

            <LatestCard
              title="Latest Solutions"
              items={dashData.latestSolutions}
              field="bannerTitle"
              onViewAll={() => navigate("/admin/solutions")}
            />

            <LatestCard
              title="Latest Blogs"
              items={dashData.latestBlogs}
              field="blogHeading"
              onViewAll={() => navigate("/admin/blogs")}
            />

            <LatestCard
              title="Latest Categories"
              items={dashData.latestCategories}
              field="categoryName"
              onViewAll={() => navigate("/admin/category")}
            />

            <LatestCard
              title="Latest Brands"
              items={dashData.latestBrands}
              field="brandName"
              onViewAll={() => navigate("/admin/brands")}
            />

            <LatestCard
              title="Latest SubCategories"
              items={dashData.latestSubCategories}
              field="subCategoryName"
              onViewAll={() => navigate("/admin/sub-category")}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
